<template>
  <div class="network-tabs" id="my-vm-tabs">
    <div class="my-vm-tabs__header">
      <div class="network-tabs-nav" role="tablist" aria-label="My VM tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="network-tab"
          :class="{ 'is-active': activeTab === tab.id }"
          :aria-selected="activeTab === tab.id ? 'true' : 'false'"
          @click="selectTab(tab.id)"
        >
          {{ tab.label }}
          <span class="network-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <p v-if="loading" class="my-vm-tabs__status">Loading…</p>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'

const props = defineProps({
  tabs: {
    type: Array,
    default: () => []
  },
  initialTab: {
    type: String,
    default: ''
  }
})

const activeTab = ref(props.initialTab || props.tabs[0]?.id || '')
const loadedTabs = ref({})
const loading = ref(false)

function updatePanels() {
  props.tabs.forEach((tab) => {
    const panel = document.getElementById(tab.panelId)
    if (!panel) return

    const isActive = tab.id === activeTab.value
    panel.classList.toggle('is-active', isActive)
    panel.style.display = isActive ? '' : 'none'
  })
}

async function loadTab(tabId) {
  const tab = props.tabs.find((entry) => entry.id === tabId)
  if (!tab?.dataUrl || !tab?.eventName || loadedTabs.value[tabId]) {
    return
  }

  loading.value = true
  const startedAt = Date.now()
  document.dispatchEvent(new CustomEvent('scc:lazy-results:loading', {
    detail: {
      eventName: tab.eventName,
      loading: true
    }
  }))

  try {
    const response = await fetch(buildTabUrl(tab.dataUrl), {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }

    const payload = await response.json()
    document.dispatchEvent(new CustomEvent(tab.eventName, {
      detail: payload?.results_data || {}
    }))
    loadedTabs.value = { ...loadedTabs.value, [tabId]: true }
  } finally {
    const elapsed = Date.now() - startedAt
    if (elapsed < 250) {
      await new Promise((resolve) => window.setTimeout(resolve, 250 - elapsed))
    }
    loading.value = false
    document.dispatchEvent(new CustomEvent('scc:lazy-results:loading', {
      detail: {
        eventName: tab.eventName,
        loading: false
      }
    }))
  }
}

async function selectTab(tabId) {
  activeTab.value = tabId
  updatePanels()

  try {
    await loadTab(tabId)
  } catch (error) {
    console.error('Failed to load VM tab', error)
    window.alert('Failed to load the VM list.')
  }
}

onMounted(() => {
  updatePanels()
  selectTab(activeTab.value)
})

function buildTabUrl(dataUrl) {
  const url = new URL(dataUrl, window.location.origin)
  const currentSearch = new URLSearchParams(window.location.search)

  currentSearch.forEach((value, key) => {
    url.searchParams.append(key, value)
  })

  return url.toString()
}
</script>
